import React, { useEffect, useMemo, useState } from "react";
import { X, AlertTriangle, Search, Check, HelpCircle, Trash2, ChevronLeft, ChevronRight, ChevronDown, Bell, Mail } from "lucide-react";
import { Button, Spinner, Switch } from "./ui/kit";
import api, { apiError } from "../lib/api";
import { fmtTime, toInputValue } from "../lib/format";
import Segment from "./Segment";
import Vyber from "./Vyber";
import DatumCas from "./DatumCas";
import OsobaKolecko from "./OsobaKolecko";
import { useAuth } from "../context/AuthContext";
import { ikonaTypu, kratkaJmena, UPOZORNENI, textUpozorneni, pripominkaEmailem } from "../lib/typyAktivit";
import ZapnoutUpozorneni from "./ZapnoutUpozorneni";

// Mřížka týdne je 7–19 h, ale roztáhne se podle událostí v týdnu (a nové
// aktivity) — večerní schůzka nebo brzký výjezd musí být v náhledu vidět.
function hodinyTydne(akce, novyOd, novyDo) {
  let od = 7, doH = 19;
  const zahrn = (sIso, eIso) => {
    const a = new Date(sIso), b = new Date(eIso || sIso);
    od = Math.min(od, a.getHours());
    doH = Math.max(doH, sameDay(a, b) ? b.getHours() + (b.getMinutes() > 0 ? 1 : 0) : 24);
  };
  akce.forEach((x) => zahrn(x.date_start, x.date_end));
  zahrn(novyOd, novyDo);
  doH = Math.min(24, Math.max(doH, od + 1));
  return Array.from({ length: doH - od }, (_, i) => od + i);
}
const MESICE_2P = ["ledna", "února", "března", "dubna", "května", "června", "července", "srpna", "září", "října", "listopadu", "prosince"];

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const prekryv = (a, s, e) => new Date(a.date_start) < e && new Date(a.date_end) > s;

const DNY_KR = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
const ROW_W = 40;   // výška hodiny v týdnu

function pondeli(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}

/** Rozloží překrývající se bloky jednoho dne do souběžných pruhů vedle sebe. */
function pruhy(bloky) {
  const serazene = [...bloky].sort((x, y) => x.s - y.s || y.e - x.e);
  const out = []; let shluk = []; let konecShluku = -Infinity;
  const uzavri = () => {
    const n = Math.max(1, ...shluk.map((b) => b.pruh + 1));
    shluk.forEach((b) => out.push({ ...b, n }));
    shluk = [];
  };
  serazene.forEach((b) => {
    if (b.s >= konecShluku && shluk.length) uzavri();
    const obsazene = new Set(shluk.filter((x) => x.e > b.s).map((x) => x.pruh));
    let pruh = 0; while (obsazene.has(pruh)) pruh += 1;
    shluk.push({ ...b, pruh });
    konecShluku = Math.max(konecShluku, b.e);
  });
  if (shluk.length) uzavri();
  return out;
}

/**
 * Týden vybraných lidí: co už mají (šedě, s iniciálami kdo), nová aktivita
 * oranžově, červeně když se s něčím kryje. Souběžné věci stojí vedle sebe,
 * nepřekrývají se. Klik do prázdna nastaví začátek.
 */
function TydenOsob({ tyden, lide, jmena, akce, novyOd, novyDo, onPick, onPosun }) {
  const dny = Array.from({ length: 7 }, (_, i) => { const d = new Date(tyden); d.setDate(d.getDate() + i); return d; });
  const s = new Date(novyOd), e = new Date(novyDo);
  const kryje = akce.some((a) => prekryv(a, s, e));
  const HOURS = hodinyTydne(akce, novyOd, novyDo);
  const h0 = HOURS[0];
  const pos = (od, doo) => {
    const a = new Date(od), b = new Date(doo);
    const zac = Math.max(0, (a.getHours() - h0) + a.getMinutes() / 60);
    const kon = Math.min(HOURS.length, sameDay(a, b) ? (b.getHours() - h0) + b.getMinutes() / 60 : HOURS.length);
    return { top: zac * ROW_W, height: Math.max(18, (kon - zac) * ROW_W - 2) };
  };
  const posledni = dny[6];
  const rozsah = tyden.getMonth() === posledni.getMonth()
    ? `${tyden.getDate()}.–${posledni.getDate()}. ${MESICE_2P[posledni.getMonth()]}`
    : `${tyden.getDate()}. ${MESICE_2P[tyden.getMonth()]} – ${posledni.getDate()}. ${MESICE_2P[posledni.getMonth()]}`;
  const dnes = new Date();
  return (
    <div className="flex flex-col gap-3" data-testid="week-grid">
      <div className="flex items-center gap-2">
        <span className="t-sekce font-700 text-ink">{rozsah}</span>
        <span className="flex items-center -space-x-1.5 ml-1">
          {lide.map((m) => (
            <span key={m.id} className="ring-2 ring-[#fafaf8] rounded-full" title={jmena[m.id]}>
              <OsobaKolecko id={m.id} name={m.name} url={m.photo_url} size={22} />
            </span>
          ))}
        </span>
        <span className="flex-1" />
        <button type="button" onClick={() => onPosun(-7)} aria-label="Předchozí týden" data-testid="tyden-predchozi"
          className="tap md:min-h-0 md:min-w-0 h-9 w-9 md:h-8 md:w-8 rounded-full border border-line-strong bg-white inline-flex items-center justify-center hover:bg-inputbg">
          <ChevronLeft size={15} />
        </button>
        <button type="button" onClick={() => onPosun(7)} aria-label="Další týden" data-testid="tyden-dalsi"
          className="tap md:min-h-0 md:min-w-0 h-9 w-9 md:h-8 md:w-8 rounded-full border border-line-strong bg-white inline-flex items-center justify-center hover:bg-inputbg">
          <ChevronRight size={15} />
        </button>
      </div>
      <div className="bg-white border border-line-strong rounded-[12px] overflow-hidden">
        <div className="grid grid-cols-[38px_repeat(7,minmax(0,1fr))] border-b border-line-strong bg-inputbg">
          <div />
          {dny.map((d, i) => {
            const jeDnes = sameDay(d, dnes), jeVybrany = sameDay(d, s);
            return (
              <div key={i} className={`text-center py-1.5 label-up ${jeVybrany ? "text-ink" : jeDnes ? "text-accent" : "text-muted"}`}>
                {DNY_KR[i]} <span className="amount">{d.getDate()}.</span>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-[38px_repeat(7,minmax(0,1fr))]">
          <div className="relative" style={{ height: HOURS.length * ROW_W }}>
            {HOURS.map((h, i) => (
              <div key={h} style={{ top: i * ROW_W + 2 }} className="absolute right-1.5 text-[9.5px] text-muted-2 amount">{h}:00</div>
            ))}
          </div>
          {dny.map((den, di) => {
            const jeVybrany = sameDay(den, s);
            const dnesni = akce.filter((a) => sameDay(new Date(a.date_start), den))
              .map((a) => ({ id: a.id, s: new Date(a.date_start), e: new Date(a.date_end), a }));
            if (sameDay(s, den)) dnesni.push({ id: "_novy", s, e, novy: true });
            return (
              <div key={di} className={`relative border-l border-line-softer ${jeVybrany ? "bg-accent/[0.03]" : ""}`}
                style={{ height: HOURS.length * ROW_W }}>
                {HOURS.map((h, hi) => (
                  <div key={h} style={{ top: hi * ROW_W, height: ROW_W }} className="absolute inset-x-0 border-t border-line-softer">
                    <button type="button" className="absolute inset-x-0 top-0 h-1/2 hover:bg-accent/5"
                      onClick={() => onPick(den, h, 0)} data-testid={`grid-cell-${di}-${h}-0`} aria-label={`${DNY_KR[di]} ${h}:00`} />
                    <button type="button" className="absolute inset-x-0 bottom-0 h-1/2 hover:bg-accent/5"
                      onClick={() => onPick(den, h, 30)} aria-label={`${DNY_KR[di]} ${h}:30`} />
                  </div>
                ))}
                {pruhy(dnesni).map((b) => {
                  const p = pos(b.s, b.e);
                  const styl = { top: p.top, height: p.height, left: `calc(${(b.pruh / b.n) * 100}% + 2px)`, width: `calc(${100 / b.n}% - 4px)` };
                  if (b.novy) {
                    return (
                      <div key="_novy" style={styl} data-testid="week-grid-new-block"
                        className={`absolute z-10 rounded-[6px] border-[1.5px] px-1 py-0.5 overflow-hidden pointer-events-none shadow-sm ${
                          kryje ? "bg-[#fdecea] border-danger" : "bg-[#fbe6d8] border-accent"}`}>
                        <div className={`text-[10px] font-700 truncate ${kryje ? "text-danger" : "text-[#a8410c]"}`}>Tato aktivita</div>
                        <div className="text-[9px] amount text-sec">{fmtTime(novyOd)}</div>
                      </div>
                    );
                  }
                  const kdo = (b.a.odpovedne_osoby || []).filter((u) => jmena[u]);
                  return (
                    <div key={b.id} style={styl} title={`${b.a.nazev} · ${fmtTime(b.a.date_start)}`}
                      className="absolute rounded-[6px] bg-[#ecebe7] px-1 py-0.5 overflow-hidden pointer-events-none">
                      <div className="text-[10px] font-600 text-sec truncate">{b.a.nazev}</div>
                      <div className="text-[9px] text-muted amount truncate">
                        {fmtTime(b.a.date_start)}{lide.length > 1 && kdo.length ? ` · ${kdo.map((u) => jmena[u]).join(", ")}` : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function VolbaVazby({ nazev, popis, vybrano, onClick, ikona, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={vybrano}
      className={`w-full text-left px-3 py-2.5 min-h-[44px] flex items-center gap-2.5 border-t border-line-softer first:border-t-0 ${
        vybrano ? "bg-accent/5" : "hover:bg-inputbg"}`}>
      {ikona}
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] md:text-[12.5px] font-600 text-ink truncate">{nazev}</span>
        {popis && <span className="block text-[13px] md:text-[11.5px] text-muted truncate">{popis}</span>}
      </span>
      <span className={`h-4 w-4 rounded-full border shrink-0 flex items-center justify-center ${
        vybrano ? "border-accent bg-accent" : "border-line-strong bg-white"}`}>
        {vybrano && <Check size={10} className="text-white" strokeWidth={3} />}
      </span>
    </button>
  );
}

// technologieId/defaultOblast/defaultTyp/defaultNazev: předvyplnění z kroků
// technologie („Naplánovat zaměření", „Naplánovat montáž") — aktivita se na
// technologii naváže a server podle ní hlídá brány.
export default function NovaAktivita({ onClose, onSaved, defaultClientId = null, defaultDate = null, defaultStart = null, aktivita = null, pripadId = null,
  technologieId = null, defaultOblast = null, defaultTyp = null, defaultNazev = null, zarizeniId: defaultZarizeniId = null,
  defaultOsoby = null, defaultPripadId = null }) {
  const editing = !!aktivita;
  const { user } = useAuth();
  // `defaultStart` = přesný čas (tap do mezery v ose), `defaultDate` = jen den (9:00).
  const initStart = aktivita
    ? new Date(aktivita.date_start)
    : defaultStart
    ? new Date(defaultStart)
    : defaultDate
    ? new Date(new Date(defaultDate).setHours(9, 0, 0, 0))
    : new Date(new Date().setHours(9, 0, 0, 0));
  const initEnd = aktivita ? new Date(aktivita.date_end) : new Date(initStart.getTime() + 3600000);

  const [oblast, setOblast] = useState(aktivita?.oblast || defaultOblast || "OBCHOD");
  const [typ, setTyp] = useState(aktivita?.typ || defaultTyp || "");
  const [nazev, setNazev] = useState(aktivita?.nazev || defaultNazev || "");
  const [clientId, setClientId] = useState(aktivita?.client_id || defaultClientId);
  const [start, setStart] = useState(toInputValue(initStart));
  const [end, setEnd] = useState(toInputValue(initEnd));
  // Nová aktivita bez předvybraných lidí: „Kdo to udělá“ = já. První klik na někoho
  // jiného mě nahradí (kdo úkol zakládá, ho nemusí dělat).
  const jaVychozi = !aktivita && !defaultOsoby?.length && !!user?.id;
  const [osoby, setOsoby] = useState(aktivita?.odpovedne_osoby || (jaVychozi ? [user.id] : defaultOsoby || []));
  const [jenVychozi, setJenVychozi] = useState(jaVychozi);
  const [tydenMobil, setTydenMobil] = useState(false);   // týden lidí na telefonu sbalený
  const [poznamky, setPoznamky] = useState(aktivita?.poznamky || "");
  const [upozornit, setUpozornit] = useState(aktivita?.upozornit_min ?? -1);   // minuty předem, 0 = v čas, −1 = ne
  const [emailem, setEmailem] = useState(!!aktivita?.upozornit_emailem);
  // Obchodní případ: předaný z případu/technologie je pevný, jinak se vybírá u klienta.
  const pevnyPripad = !!(pripadId || technologieId || aktivita?.technologie_id);
  // defaultPripadId = jen předvyplnění (navazující aktivita), dá se změnit.
  const [pripad, setPripad] = useState(aktivita?.obchodni_pripad_id || pripadId || defaultPripadId || "");
  const [pripady, setPripady] = useState([]);
  // Servis: na co se jede — zařízení klienta, technologie z jeho případu, nebo zatím nic.
  const [zarizeniId, setZarizeniId] = useState(aktivita?.zarizeni_id || defaultZarizeniId);
  const [techId, setTechId] = useState(aktivita?.technologie_id || technologieId || null);
  const [vazby, setVazby] = useState(null);   // {zarizeni, technologie} | null = načítám
  const [hotovo, setHotovo] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [types, setTypes] = useState([]);
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientQuery, setClientQuery] = useState("");
  const [klientOtevren, setKlientOtevren] = useState(false);

  const [tyden, setTyden] = useState(() => pondeli(initStart));   // který týden ukazuje kalendář vpravo
  const [akceDne, setAkceDne] = useState([]);
  const [kolize, setKolize] = useState([]);
  const [navrh, setNavrh] = useState(null);

  useEffect(() => {
    api.get("/api/meta/ciselniky").then((r) => setTypes(r.data.typy_udalosti || []));
    api.get("/api/team").then((r) => setMembers((r.data.members || []).filter((m) => m.active)));
    api.get("/api/clients?limit=200").then((r) => setClients(r.data.items || []));
  }, []);

  useEffect(() => {
    if (oblast !== "SERVIS" || !clientId) { setVazby(null); return; }
    let zruseno = false;
    setVazby(null);
    api.get("/api/aktivity/vazby", { params: { client_id: clientId } })
      .then((r) => { if (!zruseno) setVazby(r.data); })
      .catch(() => { if (!zruseno) setVazby({ zarizeni: [], technologie: [] }); });
    return () => { zruseno = true; };
  }, [oblast, clientId]);

  // Případy vybraného klienta (jen obchod; servis se váže na zařízení).
  useEffect(() => {
    if (!clientId || oblast !== "OBCHOD") { setPripady([]); return; }
    let zruseno = false;
    api.get("/api/obchodni-pripady", { params: { client_id: clientId, limit: 100 } })
      .then((r) => { if (!zruseno) setPripady(r.data.items || []); })
      .catch(() => { if (!zruseno) setPripady([]); });
    return () => { zruseno = true; };
  }, [clientId, oblast]);

  const vybratKlienta = (id) => {
    setClientId(id);
    // jiný klient = jiná zařízení a jiné případy; předchozí volba by k němu nepatřila
    setZarizeniId(null);
    setTechId(null);
    if (!pevnyPripad) setPripad("");
  };

  const oblastTypes = useMemo(() => types.filter((t) => t.oblast === oblast), [types, oblast]);

  useEffect(() => {
    if (!types.length) return;
    if (!oblastTypes.find((t) => t.name === typ)) {
      setTyp(oblastTypes[0]?.name || "");
    }
  }, [oblastTypes]); // eslint-disable-line

  // Lidé: přihlášený první, u stejných křestních jmen iniciála příjmení.
  const lide = useMemo(() => [...members].sort((a, b) => (a.id === user?.id ? -1 : b.id === user?.id ? 1 : 0)), [members, user?.id]);
  const jmena = useMemo(() => kratkaJmena(lide, user?.id), [lide, user?.id]);
  const vybraniLide = lide.filter((m) => osoby.includes(m.id));

  const osobyKey = osoby.join(",");
  const tydenKey = toInputValue(tyden).slice(0, 10);

  // Týden vpravo: aktivity vybraných lidí v tom týdnu.
  useEffect(() => {
    const ids = osobyKey ? osobyKey.split(",") : [];
    if (!ids.length) { setAkceDne([]); return undefined; }
    let zruseno = false;
    const od = new Date(`${tydenKey}T00:00`), doo = new Date(od); doo.setDate(doo.getDate() + 7);
    api.get("/api/aktivity", { params: { od: od.toISOString(), do: doo.toISOString() } })
      .then((w) => { if (!zruseno) setAkceDne((w.data.items || []).filter(
        (a) => a.id !== aktivita?.id && (a.odpovedne_osoby || []).some((u) => ids.includes(u)))); })
      .catch(() => {});
    return () => { zruseno = true; };
  }, [osobyKey, tydenKey, aktivita?.id]);

  // Kolize + nejbližší volný termín pro všechny vybrané (počítá server).
  useEffect(() => {
    const ids = osobyKey ? osobyKey.split(",") : [];
    if (!ids.length) { setKolize([]); setNavrh(null); return undefined; }
    const s = new Date(start), e = new Date(end);
    if (!(e > s)) return undefined;
    let zruseno = false;
    api.get("/api/aktivity/kolize", {
      params: { user_ids: ids.join(","), date_start: s.toISOString(), date_end: e.toISOString(), exclude_id: aktivita?.id },
    }).then((k) => { if (!zruseno) { setKolize(k.data.kolize || []); setNavrh(k.data.navrh || null); } }).catch(() => {});
    return () => { zruseno = true; };
  }, [osobyKey, start, end, aktivita?.id]);

  const delka = () => {
    const d = new Date(end) - new Date(start);
    return d > 0 ? d : 3600000;
  };
  // Výchozí délka je hodina. Změna začátku posune konec o stejnou délku,
  // takže DO je vždy po OD (i když se začátek přesune na jiný den).
  const zmenitOd = (v) => {
    if (!v) return;
    const d = delka();
    setStart(v);
    setEnd(toInputValue(new Date(new Date(v).getTime() + d)));
    setTyden(pondeli(new Date(v)));
  };
  const zmenitDo = (v) => {
    if (!v) return;
    if (new Date(v) <= new Date(start)) {
      // konec před začátkem nejde — vrátíme ho na výchozí hodinu po začátku
      setEnd(toInputValue(new Date(new Date(start).getTime() + 3600000)));
      return;
    }
    setEnd(v);
  };
  const pickSlot = (d, hour, minute) => {
    const s = new Date(d); s.setHours(hour, minute, 0, 0);
    zmenitOd(toInputValue(s));
  };
  const applyNavrh = () => {
    setStart(toInputValue(new Date(navrh.date_start)));
    setEnd(toInputValue(new Date(navrh.date_end)));
    setTyden(pondeli(new Date(navrh.date_start)));
  };
  const posunTyden = (o) => setTyden((d) => { const x = new Date(d); x.setDate(x.getDate() + o); return x; });

  // Kdo s čím koliduje — jménem, ať je hned jasné, koho se to týká.
  const kolizeText = useMemo(() => {
    if (!kolize.length) return "";
    const k = kolize[0];
    const kdo = (k.odpovedne_osoby || []).filter((u) => osoby.includes(u)).map((u) => jmena[u]).filter(Boolean);
    const kdoText = kdo.length ? (kdo[0] === "Já" ? "Máš" : `${kdo.join(", ")} má`) : "Někdo z vybraných má";
    return `${kdoText} v ${fmtTime(k.date_start)} ${k.nazev || "jinou aktivitu"}${kolize.length > 1 ? ` (+${kolize.length - 1})` : ""}`;
  }, [kolize, osoby, jmena]);

  const toggleOsoba = (id) => {
    if (jenVychozi) {
      setJenVychozi(false);
      if (id !== user?.id) { setOsoby([id]); return; }
    }
    setOsoby((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const filteredClients = clients.filter((c) => {
    const q = clientQuery.toLowerCase();
    return !q || (c.display_name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q);
  });
  const selectedClient = clients.find((c) => c.id === clientId);

  // Upravit a smazat musí jít odevšad, kde se aktivita otevře k úpravě
  // (kalendář, klient, zařízení, případ) — dřív šlo smazat jen z detailu.
  const smazat = async () => {
    if (!window.confirm("Opravdu smazat tuto aktivitu?")) return;
    setError(""); setSaving(true);
    try {
      await api.delete(`/api/aktivity/${aktivita.id}`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError(apiError(e.response?.data?.detail) || e.message);
      setSaving(false);
    }
  };

  const submit = async () => {
    setError("");
    if (!typ) return setError("Vyber typ aktivity.");
    if (!nazev.trim()) return setError("Zadej název aktivity.");
    if (!(new Date(end) > new Date(start))) return setError("Konec musí být po začátku.");
    setSaving(true);
    const payload = {
      oblast, typ, nazev: nazev.trim(), client_id: clientId || null,
      obchodni_pripad_id: oblast === "OBCHOD" ? (pripad || null) : (aktivita?.obchodni_pripad_id || pripadId || null),
      technologie_id: oblast === "SERVIS"
        ? (techId || (editing ? "" : null))
        : (aktivita?.technologie_id || technologieId || null),
      // zařízení jen u servisu; "" při úpravě = vazbu zrušit
      zarizeni_id: oblast === "SERVIS" ? (zarizeniId || (editing ? "" : null)) : null,
      date_start: new Date(start).toISOString(), date_end: new Date(end).toISOString(),
      odpovedne_osoby: osoby,
      poznamky,
      upozornit_min: upozornit,
      upozornit_emailem: emailem && upozornit >= 0 && pripominkaEmailem(typ),
    };
    try {
      if (editing) await api.patch(`/api/aktivity/${aktivita.id}`, payload);
      else {
        const { data } = await api.post("/api/aktivity", payload);
        // „Už je hotovo" přes stejný krok jako odškrtnutí — ať proběhne i workflow a brány.
        if (hotovo && data?.id) await api.post(`/api/aktivity/${data.id}/hotovo`);
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Bez vybraných lidí dostane upozornění autor úkolu (při úpravě cizího úkolu ne já).
  const cizi = editing && aktivita.created_by && aktivita.created_by !== user?.id;
  const casStartu = (start || "").slice(11, 16).replace(/^0/, "");
  const velke = (t) => t.charAt(0).toUpperCase() + t.slice(1);
  const jenJa = osoby.length === 1 && osoby[0] === user?.id;
  // Na telefonu se klávesnice sama neotevírá — zakryla by půlku formuláře a posunula ho.
  const dotyk = typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;

  return (
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid="nova-aktivita">
      <div className="absolute inset-0 bg-black/40 zaclon-in" onClick={onClose} />
      <div className="okno-in relative bg-white w-full md:max-w-activity md:rounded-[20px] rounded-t-sheet shadow-panel max-h-[94dvh] md:max-h-[92vh] flex flex-col overflow-hidden"
        role="dialog" aria-modal="true">
        <div className="bg-white border-b border-line-softer px-5 md:px-6 py-3.5 flex items-center gap-3 shrink-0">
          <h2 className="t-sekce font-800 text-ink">{editing ? "Upravit aktivitu" : "Nová aktivita"}</h2>
          <Segment className="inline-flex p-[3px] bg-inputbg rounded-pill">
            {["OBCHOD", "SERVIS"].map((o) => (
              <button data-on={oblast === o} key={o} onClick={() => setOblast(o)} data-testid={`oblast-${o}`}
                className={`pill px-3 h-7 text-[12.5px] font-700 ${oblast === o ? "text-white" : "text-sec"}`}>
                {o === "OBCHOD" ? "Obchod" : "Servis"}
              </button>
            ))}
          </Segment>
          <span className="flex-1" />
          <button onClick={onClose} className="tap md:min-h-0 md:min-w-0 inline-flex items-center justify-center text-muted hover:text-ink" data-testid="nova-aktivita-close" aria-label="Zavřít">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden lg:overflow-hidden lg:grid lg:grid-cols-2">
          {/* vlevo: co, kdo, kdy, ke komu (Pipedrive) */}
          <div className="p-5 md:p-6 space-y-5 lg:overflow-y-auto">
            <input className="field !h-12 !text-[17px] font-600" value={nazev} onChange={(e) => setNazev(e.target.value)}
              placeholder="Co je potřeba udělat?" autoFocus={!editing && !dotyk} data-testid="aktivita-nazev" aria-label="Název" />

            {/* Typ: ikony stejně široké přes celou šířku (vejdou se i na telefon bez
                posouvání), název vybraného typu je napsaný nad nimi. */}
            <div>
              <div className="flex items-baseline gap-2 mb-1.5 min-w-0">
                <span className="label-up text-muted shrink-0">Typ</span>
                <span className="t-telo md:text-[13px] font-700 text-ink truncate" data-testid="typ-vybrany">{typ || "vyber"}</span>
              </div>
            <Segment className="grid p-1 bg-inputbg rounded-pill" data-testid="typ-list"
              style={{ gridTemplateColumns: `repeat(${Math.max(oblastTypes.length, 1)}, minmax(0, 1fr))` }}
              role="radiogroup" aria-label="Typ aktivity">
              {oblastTypes.map((t) => {
                const on = typ === t.name;
                const Ikona = ikonaTypu(t.name);
                return (
                  <button key={t.name} type="button" data-on={on} onClick={() => setTyp(t.name)} data-testid={`typ-${t.name}`}
                    role="radio" aria-checked={on} aria-label={t.name} title={t.name}
                    className={`relative md:min-h-0 h-11 md:h-9 min-w-0 rounded-pill inline-flex items-center justify-center ${
                      on ? "text-white" : "text-sec hover:text-ink"}`}>
                    <Ikona size={18} strokeWidth={2} className="shrink-0" />
                  </button>
                );
              })}
            </Segment>
            </div>

            <div>
              <span className="label-up text-muted block mb-2">Kdo to udělá</span>
              <div className="flex flex-wrap gap-1.5">
                {lide.map((m) => {
                  const on = osoby.includes(m.id);
                  return (
                    <button key={m.id} type="button" onClick={() => toggleOsoba(m.id)} data-testid={`osoba-${m.id}`} aria-pressed={on}
                      className={`tap md:min-h-0 pill pl-1 pr-3 h-11 md:h-9 flex items-center gap-2 text-[13px] md:text-[12.5px] font-600 border transition-colors ${
                        on ? "bg-ink border-ink text-white" : "bg-white border-line-strong text-sec hover:bg-inputbg"}`}>
                      <OsobaKolecko id={m.id} name={m.name} url={m.photo_url} size={28} />
                      {jmena[m.id]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="block min-w-0">
                <span className="label-up text-muted block mb-1.5">Od</span>
                <DatumCas className="field" value={start} onChange={zmenitOd} data-testid="aktivita-start" />
              </div>
              <div className="block min-w-0">
                <span className="label-up text-muted block mb-1.5">Do</span>
                <DatumCas className="field" value={end} min={start} onChange={zmenitDo} data-testid="aktivita-end" />
              </div>
            </div>

            {/* Upozornění: v čas nebo předem do zvonečku, na telefon a do počítače; u návštěv i e-mailem. */}
            <div className="rounded-[14px] border border-line-strong px-3.5 py-3 flex flex-col gap-3" data-testid="upozorneni-ukolu">
              <div className="flex items-center gap-3">
                <Bell size={17} strokeWidth={1.9} className="text-sec shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block t-telo md:text-[13px] font-600 text-ink">Upozornit</span>
                  <span className="block t-meta text-muted" data-testid="upozorneni-popis">
                    {upozornit < 0 ? "Na telefonu a v počítači — v čas nebo předem."
                      : `${upozornit === 0 ? `V ${casStartu}` : velke(textUpozorneni(upozornit))} ${
                        jenJa ? "ti blikne na telefonu i v počítači" : osoby.length ? "blikne všem, kdo to udělají"
                          : cizi ? "blikne autorovi úkolu" : "ti blikne na telefonu i v počítači"
                      }${emailem && pripominkaEmailem(typ) ? " a přijde e-mail" : ""}.`}
                  </span>
                </span>
                <Switch checked={upozornit >= 0} onChange={(on) => setUpozornit(on ? (pripominkaEmailem(typ) ? 1440 : 0) : -1)}
                  testid="upozornit-prepinac" label="Upozornit" />
              </div>
              {upozornit >= 0 && (
                <>
                  <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Kdy upozornit">
                    {UPOZORNENI.map(([min, text]) => {
                      const on = upozornit === min;
                      return (
                        <button key={min} type="button" role="radio" aria-checked={on} onClick={() => setUpozornit(min)}
                          data-testid={`upozornit-${min}`}
                          className={`tap md:min-h-0 pill h-11 md:h-8 px-3.5 border t-meta font-600 transition-colors ${
                            on ? "bg-ink border-ink text-white" : "bg-white border-line-strong text-sec hover:bg-inputbg"}`}>
                          {text}
                        </button>
                      );
                    })}
                  </div>
                  {pripominkaEmailem(typ) && (
                    <div className="flex items-center gap-3 pt-3 border-t border-line-softer" data-testid="upozornit-emailem">
                      <Mail size={17} strokeWidth={1.9} className="text-sec shrink-0" />
                      <span className="flex-1 min-w-0 t-telo md:text-[13px] font-600 text-ink">Poslat i e-mailem</span>
                      <Switch checked={emailem} onChange={setEmailem} testid="upozornit-emailem-prepinac" label="Poslat i e-mailem" />
                    </div>
                  )}
                  <ZapnoutUpozorneni varianta="radek" />
                </>
              )}
            </div>

            <div className={`grid gap-3 ${oblast === "OBCHOD" && clientId ? "md:grid-cols-2" : ""}`}>
              <div className="block relative">
                <span className="label-up text-muted block mb-1.5">Klient</span>
                {selectedClient ? (
                  <div className="field flex items-center gap-2">
                    <span className="flex-1 min-w-0 truncate font-600">{selectedClient.display_name}</span>
                    <button type="button" onClick={() => vybratKlienta(null)} aria-label="Zrušit klienta" data-testid="client-zrusit"
                      className="tap md:min-h-0 md:min-w-0 inline-flex items-center justify-center text-muted hover:text-ink"><X size={15} /></button>
                  </div>
                ) : (
                  <>
                    <div className="field flex items-center gap-2">
                      <Search size={15} className="text-muted shrink-0" />
                      <input className="bg-transparent outline-none w-full" placeholder="Hledat klienta…"
                        value={clientQuery} onChange={(e) => { setClientQuery(e.target.value); setKlientOtevren(true); }}
                        onFocus={() => setKlientOtevren(true)} onBlur={() => setTimeout(() => setKlientOtevren(false), 150)}
                        data-testid="client-search" />
                    </div>
                    {klientOtevren && (
                      <div className="vyber-menu absolute z-20 left-0 right-0 top-[calc(100%+4px)] bg-white rounded-card border border-line-softer shadow-panel p-1.5 max-h-64 overflow-y-auto">
                        {filteredClients.slice(0, 8).map((c) => (
                          <button key={c.id} type="button" onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { vybratKlienta(c.id); setClientQuery(""); setKlientOtevren(false); }}
                            data-testid={`client-opt-${c.id}`}
                            className="vyber-polozka w-full text-left px-2.5 min-h-[40px] rounded-[8px] t-telo md:text-[13px] text-ink">
                            {c.display_name} {c.adresa?.mesto && <span className="text-muted">· {c.adresa.mesto}</span>}
                          </button>
                        ))}
                        {!filteredClients.length && <div className="px-2.5 py-2 t-meta text-muted">Nic nenalezeno</div>}
                      </div>
                    )}
                  </>
                )}
              </div>
              {oblast === "OBCHOD" && clientId && (
                <label className="block">
                  <span className="label-up text-muted block mb-1.5">Obchodní případ</span>
                  <Vyber className="field" value={pripad} disabled={pevnyPripad} onChange={(e) => setPripad(e.target.value)}
                    data-testid="aktivita-pripad">
                    <option value="">Bez případu</option>
                    {pripad && !pripady.some((p) => p.id === pripad) && <option value={pripad}>{aktivita?.obchodni_pripad?.nazev || "Vybraný případ"}</option>}
                    {pripady.map((p) => <option key={p.id} value={p.id}>{p.nazev}</option>)}
                  </Vyber>
                </label>
              )}
            </div>

            {oblast === "SERVIS" && clientId && (
              <div data-testid="vyber-zarizeni">
                <span className="label-up text-muted block mb-1.5">Zařízení</span>
                {vazby === null ? (
                  <div className="field flex items-center gap-2 text-muted"><Spinner /> Načítám zařízení klienta…</div>
                ) : (
                  <div className="border border-line-strong rounded-input overflow-hidden max-h-60 overflow-y-auto">
                    {vazby.zarizeni.map((z) => (
                      <VolbaVazby key={z.id} vybrano={zarizeniId === z.id} testid={`zarizeni-opt-${z.id}`}
                        onClick={() => { setZarizeniId(z.id); setTechId(null); }}
                        nazev={z.nazev} popis={[z.kategorie, z.detail, z.stav === "MONTAZ" ? "v montáži" : ""].filter(Boolean).join(" · ")} />
                    ))}
                    {vazby.technologie.length > 0 && (
                      <div className="label-up text-muted px-3 pt-2.5 pb-1 bg-inputbg border-t border-line-softer">Z obchodního případu</div>
                    )}
                    {vazby.technologie.map((t) => (
                      <VolbaVazby key={t.id} vybrano={!zarizeniId && techId === t.id} testid={`technologie-opt-${t.id}`}
                        onClick={() => { setTechId(t.id); setZarizeniId(null); }}
                        nazev={t.nazev} popis={[t.pripad, t.faze].filter(Boolean).join(" · ")} />
                    ))}
                    {techId && !zarizeniId && !vazby.technologie.some((t) => t.id === techId) && (
                      // technologie předaná z kroku zakázky (montáž, předání) — v seznamu být nemusí
                      <VolbaVazby vybrano testid="technologie-predana" onClick={() => {}}
                        nazev="Technologie z obchodního případu" popis="Aktivita se naváže na krok zakázky." />
                    )}
                    <VolbaVazby vybrano={!zarizeniId && !techId} testid="zarizeni-nezname"
                      onClick={() => { setZarizeniId(null); setTechId(null); }}
                      ikona={<HelpCircle size={15} className="text-muted" />}
                      nazev="Zařízení zatím neznáme"
                      popis={vazby.zarizeni.length || vazby.technologie.length
                        ? "Doplníš ho na místě nebo v servisním zápisu."
                        : "Klient zatím nemá žádné zařízení v evidenci."} />
                  </div>
                )}
              </div>
            )}

            {/* Popis — ukládá se do `poznamky`. */}
            <label className="block">
              <span className="label-up text-muted block mb-1.5">Popis</span>
              <textarea rows={3} className="field resize-y !h-auto py-2.5" value={poznamky}
                onChange={(e) => setPoznamky(e.target.value)}
                placeholder="Co přesně udělat, podklady, na co nezapomenout…"
                data-testid="aktivita-poznamky" />
            </label>
          </div>

          {/* vpravo: týden vybraných lidí, kolize. Na telefonu je týden sbalený pod tlačítkem
              a prázdná nápověda se neukazuje — formulář končí popisem. */}
          <div className={`border-t lg:border-t-0 lg:border-l border-line-softer bg-[#fafaf8] p-4 md:p-5 lg:overflow-y-auto space-y-3 ${
            vybraniLide.length || kolize.length ? "" : "hidden lg:block"}`}>
            {vybraniLide.length > 0 && (
              <button type="button" onClick={() => setTydenMobil((v) => !v)} aria-expanded={tydenMobil} data-testid="tyden-mobil"
                className="lg:hidden tap w-full flex items-center gap-2 text-left">
                <span className="flex-1 min-w-0 t-telo font-600 text-ink truncate">
                  Týden: {vybraniLide.map((m) => jmena[m.id]).join(", ")}
                </span>
                <ChevronDown size={18} className={`text-muted shrink-0 transition-transform ${tydenMobil ? "rotate-180" : ""}`} />
              </button>
            )}
            {vybraniLide.length ? (
              <div className={tydenMobil ? "" : "hidden lg:block"}>
                <TydenOsob tyden={tyden} lide={vybraniLide} jmena={jmena} akce={akceDne} novyOd={start} novyDo={end}
                  onPick={pickSlot} onPosun={posunTyden} />
              </div>
            ) : (
              <div className="h-full min-h-[160px] flex items-center justify-center text-center t-meta text-muted px-6">
                Vyber, kdo to udělá — uvidíš jeho týden a upozorním tě, když už má něco jiného.
              </div>
            )}
            {kolize.length > 0 && (
              <div className="rounded-[12px] bg-[#fff6e8] px-3 py-2.5 flex flex-wrap items-center gap-x-2 gap-y-1.5" data-testid="kolize-warning">
                <AlertTriangle size={15} className="text-[#a0610c] shrink-0" />
                <span className="t-meta font-600 text-[#a0610c] flex-1 min-w-0">{kolizeText}</span>
                {navrh && (
                  <button type="button" onClick={applyNavrh} data-testid="navrh-apply"
                    className="tap md:min-h-0 t-meta font-700 text-[#a0610c] underline underline-offset-2">
                    Posunout na {sameDay(new Date(navrh.date_start), new Date(start)) ? "" : `${new Date(navrh.date_start).getDate()}. ${new Date(navrh.date_start).getMonth() + 1}. `}{fmtTime(navrh.date_start)}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {error && <div className="px-6 pt-2 text-[12.5px] text-danger">{error}</div>}
        {/* Telefon: velké Uložit přes celou šířku s odstupem od spodní hrany, zavírá se křížkem
            nahoře. Počítač: Zrušit + Uložit vpravo. */}
        <div className="bg-white border-t border-line-softer px-4 md:px-6 pt-3 md:py-3.5 flex items-center gap-2.5 shrink-0 pb-[max(20px,env(safe-area-inset-bottom))]">
          {editing ? (
            <button onClick={smazat} disabled={saving} data-testid="aktivita-smazat" aria-label="Smazat aktivitu"
              className="tap shrink-0 h-[52px] w-[52px] md:h-auto md:w-auto md:min-h-0 rounded-full md:rounded-none bg-[#fdecea] md:bg-transparent inline-flex items-center justify-center gap-1.5 t-meta font-700 text-danger md:hover:underline disabled:opacity-50">
              <Trash2 size={18} className="md:hidden" /><Trash2 size={15} className="hidden md:block" />
              <span className="hidden md:inline">Smazat</span>
            </button>
          ) : (
            <label className={`tap shrink-0 h-[52px] md:h-auto md:min-h-0 px-4 md:px-0 rounded-full md:rounded-none border md:border-0 inline-flex items-center gap-2.5 t-telo md:text-[13px] font-600 cursor-pointer transition-colors ${
              hotovo ? "border-success text-success md:text-sec" : "border-line-strong text-sec"}`}>
              <input type="checkbox" checked={hotovo} onChange={(e) => setHotovo(e.target.checked)} data-testid="aktivita-uz-hotovo" />
              <span className="md:hidden">Hotovo</span><span className="hidden md:inline">Už je hotovo</span>
            </label>
          )}
          <span className="hidden md:block flex-1" />
          <button onClick={onClose} className="btn-ghost hidden md:inline-flex !h-11 !px-5">Zrušit</button>
          <Button onClick={submit} disabled={saving} data-testid="aktivita-save"
            className="flex-1 md:flex-none !h-[52px] md:!h-11 !px-6 text-[16px] md:text-[14px] !font-700">
            {saving ? <Spinner /> : editing ? "Uložit změny" : "Uložit"}
          </Button>
        </div>
      </div>
    </div>
  );
}
